import type { CrmNoteType } from '@plato/data/prisma';
import { BanIcon, NotebookPen, PaperclipIcon, X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useController, useWatch } from 'react-hook-form';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { FileCard } from '../../crm-attachment/components/file-card.tsx';
import { useFileUploader } from '../../crm-attachment/hooks/use-file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { ImproveTextButton } from '../../improve-text/components/improve-text-button.tsx';
import { VoiceRecordingButton } from '../../voice-recording/components/voice-recording-button.tsx';
import { NoteFormFields } from './note-form-fields.tsx';

export type NoteFormSectionProps<T extends CrmNoteInput> = {
    noteType: CrmNoteType;
    control: Control<T>;
    errors: FieldErrors<T>;
    noteId?: string;
    onImproveTextAccepted?: (sessionId: string) => void;
    onVoiceTranscription: (text: string) => void;
};

export function NoteFormSection<T extends CrmNoteInput>({
    noteType,
    control,
    errors,
    noteId,
    onImproveTextAccepted,
    onVoiceTranscription,
}: NoteFormSectionProps<T>): ReactNode {
    const { t } = useLocale();
    const { enableFileUpload } = useAppConfig();

    const controlAsNote = control as Control<CrmNoteInput>;

    const titleField = useController({ name: 'title', control: controlAsNote });
    const attachmentsField = useController({ name: 'attachments', control: controlAsNote });
    const attachments = attachmentsField.field.value;
    const fileUploader = useFileUploader({
        value: attachments ?? [],
        onChange: attachmentsField.field.onChange,
        analyticsContext: 'note',
    });

    const interactionHappened = useWatch({ name: 'interactionHappened', control: controlAsNote });
    const notReached = noteType !== 'NOTE' && interactionHappened === false;

    return (
        <>
            <DialogForm.Item className="items-center justify-between">
                <DialogForm.Label icon={NotebookPen}>
                    {t('notes__form__protokoll_label', { ns: 'notes' })}
                </DialogForm.Label>
                <div className="flex gap-1">
                    <ImproveTextButton
                        text={titleField.field.value}
                        entityId={noteId}
                        onConfirmation={(improved, sessionId) => {
                            titleField.field.onChange(improved);
                            onImproveTextAccepted?.(sessionId);
                        }}
                    />
                    {enableFileUpload && (
                        <Button
                            type="button"
                            variant="icon"
                            size="icon"
                            icon={PaperclipIcon}
                            onClick={fileUploader.open}
                            disabled={!fileUploader.isReady}
                            aria-label={t('attachments__add_files', { ns: 'common' })}
                        />
                    )}
                    <VoiceRecordingButton
                        trackingContext={{ source: 'customer_interaction', note_type: noteType }}
                        onTranscription={onVoiceTranscription}
                    />
                </div>
            </DialogForm.Item>
            {attachments && attachments.length > 0 && (
                <div className="flex min-w-0 w-full flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                    {attachments.map((file) => (
                        <div key={file.externalUuid} className="w-40 shrink-0">
                            <FileCard
                                file={file}
                                onRemove={fileUploader.remove}
                                onEdit={fileUploader.edit}
                                onDownload={fileUploader.download}
                            />
                        </div>
                    ))}
                </div>
            )}
            {fileUploader.uploaderElement}
            <div className="flex flex-col gap-1.5">
                <NoteFormFields
                    noteType={noteType}
                    control={control}
                    errors={errors}
                    disableVoiceRecording={noteType !== 'NOTE'}
                    placeholderOverride={
                        notReached ? t('notes__not_reached__reason_placeholder', { ns: 'notes' }) : undefined
                    }
                />
                {noteType !== 'NOTE' && (
                    <Controller
                        name="interactionHappened"
                        control={controlAsNote}
                        render={({ field }) => {
                            const notReached = field.value === false;

                            if (notReached) {
                                return (
                                    <div className="flex items-center gap-1 self-start px-2 py-1">
                                        <Text color="neutral-950">
                                            {t('notes__not_reached__active_label', { ns: 'notes' })}
                                        </Text>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => {
                                                field.onChange(true);
                                            }}
                                            aria-label={t('notes__not_reached__back', { ns: 'notes' })}
                                            className="h-auto min-w-0 rounded p-1 text-neutral-700 hover:text-neutral-950"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                );
                            }

                            return (
                                <Button
                                    type="button"
                                    variant="subtle"
                                    rounded
                                    leftIcon={BanIcon}
                                    onClick={() => {
                                        field.onChange(false);
                                    }}
                                    className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                >
                                    {t('notes__not_reached__active_label', { ns: 'notes' })}
                                </Button>
                            );
                        }}
                    />
                )}
            </div>
        </>
    );
}
